import React, { useState, useEffect, useCallback } from 'react';
import { Popover, Drawer } from 'antd';
import { observer } from 'mobx-react';
import {
  PopoverContainer,
  ConnectionInfo,
  EntityName,
  ArrowIcon,
  OwnershipSection,
  OwnershipLabel,
  OwnershipInputWrapper,
  OwnershipInput,
  ButtonContainer,
  DeleteButton,
  SaveButton,
} from './EdgeEditPopover.styles';
import { EditConnectionArrowRightIcon } from '../../assets/icons';
import NumberFormat from 'react-number-format';
import { useStore } from '../../../app/data/root.store';

export const EdgeEditPopover = observer(({
  open,
  edge,
  children,
}) => {
  const { entitiesStore, uiStore } = useStore();
  const [edgeOwnership, setEdgeOwnership] = useState(edge?.ownershipPercentage || (edge?.isNewConnection ? '' : ''));

  const handleSave = useCallback(async () => {
    if (!edge) return;

    try {
      if (edge.isNewConnection) {
        await entitiesStore.createConnection(
          edge.parentBeneficiaryId,
          edge.childBeneficiaryId,
          (edgeOwnership ? parseFloat(edgeOwnership) : undefined)
        );
      } else {
        await entitiesStore.updateConnection(edge.id, {
          ownershipPercentage: parseFloat(edgeOwnership) || undefined
        });
      }
      entitiesStore.closePopover();
    } catch (error) {
      console.error('Error saving connection:', error);
    }
  }, [edge, edgeOwnership, entitiesStore]);

  const handleDelete = useCallback(async () => {
    if (!edge) return;

    try {
      if (edge.isNewConnection) {
        // For new connections, remove the temporary edge
        entitiesStore.removeTemporaryEdge(edge.id);
      } else {
        await entitiesStore.deleteConnection(edge.id);
      }
    } catch (error) {
      console.error('Error deleting connection:', error);
    } finally {
      entitiesStore.closePopover();
    }
  }, [edge, entitiesStore]);

  const handleKeyDown = useCallback((event) => {
    if (!open) return;
    if (event.key === 'Escape') entitiesStore.closePopover();
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSave();
    }
  }, [open, handleSave, entitiesStore]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const getEntityName = (entityId) => {
    const entity = entitiesStore.getEntityById(entityId);
    return entity?.name || 'Unknown Entity';
  };

  const popoverContent = (
    <PopoverContainer onClick={(e) => e.stopPropagation()}>
      <ConnectionInfo>
        <EntityName>{getEntityName(edge?.parentBeneficiaryId)}</EntityName>
        <ArrowIcon>
          <EditConnectionArrowRightIcon />
        </ArrowIcon>
        <EntityName>{getEntityName(edge?.childBeneficiaryId)}</EntityName>
      </ConnectionInfo>
      <OwnershipSection>
        <OwnershipLabel>Ownership</OwnershipLabel>
        <OwnershipInputWrapper>
          <NumberFormat
            thousandSeparator={true}
            customInput={OwnershipInput}
            value={edgeOwnership}
            suffix={'%'}
            placeholder={'%'}
            autoComplete="off"
            onValueChange={(values) => { setEdgeOwnership(values.floatValue) }}
            disabled={entitiesStore.isCreatingConnection || entitiesStore.isDeletingConnection || entitiesStore.isUpdatingConnection}
            decimalScale={2}
            autoFocus
            allowNegative={false}
            isAllowed={({ floatValue }) => floatValue === undefined || (floatValue >= 0 && floatValue <= 100)}
          />
        </OwnershipInputWrapper>
      </OwnershipSection>
      <ButtonContainer>
        {edge?.isNewConnection ? (
          <>
            <DeleteButton
              onClick={handleDelete}
              disabled={entitiesStore.isDeletingConnection}
            >
              Cancel
            </DeleteButton>
            <SaveButton
              onClick={handleSave}
              disabled={entitiesStore.isCreatingConnection}
            >
              {entitiesStore.isCreatingConnection ? 'Creating...' : 'Save'}
            </SaveButton>
          </>
        ) : (
          <>
            <DeleteButton
              onClick={handleDelete}
              disabled={entitiesStore.isDeletingConnection}
            >
              {entitiesStore.isDeletingConnection ? 'Deleting...' : 'Delete'}
            </DeleteButton>
            <SaveButton
              onClick={handleSave}
              disabled={entitiesStore.isUpdatingConnection}
            >
              {entitiesStore.isUpdatingConnection ? 'Saving...' : 'Save'}
            </SaveButton>
          </>
        )}
      </ButtonContainer>
    </PopoverContainer>
  );

  // Mobile drawer version
  if (!uiStore.isDesktopView) {
    return (
      <>
        {children}
        <Drawer
          closable={false}
          visible={open}
          width="100%"
          height="auto"
          className="edgeEditDrawerContainer_mobile"
          placement="bottom"
          destroyOnClose={true}
          onClose={entitiesStore.closePopover}
        >
          {popoverContent}
        </Drawer>
      </>
    );
  }

  // Desktop popover version
  return (
    <Popover
      content={popoverContent}
      trigger={['click']}
      visible={open}
      placement="rightBottom"
      overlayClassName='edge_edit_popover_wrapper'
      destroyTooltipOnHide={true}
    >
      {children}
    </Popover>
  );
}); 